export const cleanText = (value) => String(value ?? "").trim();

export const firstText = (...values) => {
  for (const value of values) {
    const text = cleanText(value);
    if (text) return text;
  }
  return "";
};

export const firstNumber = (...values) => {
  for (const value of values) {
    const normalized = String(value ?? "").replace(/,/g, "").trim();
    if (!normalized) continue;
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

export const safeFilePart = (value, fallback = "PACS") =>
  cleanText(value || fallback)
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 100) || fallback;

export const formatInr = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });

export const formatPortalDate = (value, fallback = "") => {
  if (!value) return fallback;
  const date = value?.toDate instanceof Function ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return cleanText(value) || fallback;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const dateInputValue = (value) => {
  if (!value) return "";
  const date = value?.toDate instanceof Function ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const addYearsToDate = (value, years) => {
  if (!value || !Number(years)) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setFullYear(date.getFullYear() + Number(years));
  return date.toISOString().slice(0, 10);
};

export function getPacsIdentity(profile = {}, project = {}) {
  const details = project.pacsApplicationDetails || {};
  const land = project.landLeaseDetails || {};

  const societyName = firstText(
    project.societyName,
    project.pacsName,
    project.organizationName,
    details.societyName,
    profile.organizationName,
    profile.societyName,
    profile.pacsName,
    profile.name,
  );
  const landAreaSqFt = firstText(
    land.landAreaSqFt,
    project.landAreaSqFt,
    details.landAreaSqFt,
  );

  return {
    societyName,
    registrationNumber: firstText(
      project.registrationNumber,
      project.registrationNo,
      project.pacsRegistrationNo,
      details.registrationNo,
      profile.registrationNumber,
      profile.registrationNo,
    ),
    registrationDate: firstText(
      project.registrationDate,
      details.registrationDate,
      profile.registrationDate,
    ),
    registrationAct: firstText(
      project.registrationAct,
      details.registrationAct,
      profile.registrationAct,
    ),
    address: firstText(
      project.address,
      project.registeredAddress,
      details.address,
      profile.address,
    ),
    state: firstText(project.state, details.state, profile.state, "Gujarat"),
    district: firstText(project.district, details.district, profile.district),
    taluka: firstText(
      project.taluka,
      project.block,
      details.taluka,
      profile.taluka,
      profile.block,
    ),
    village: firstText(project.village, details.village, profile.village),
    pinCode: firstText(project.pinCode, project.pincode, details.pinCode, profile.pinCode),
    applicationId: firstText(project.applicationId, project.projectId, project.id),
    chairmanName: firstText(
      project.chairmanName,
      details.chairmanName,
      profile.chairmanName,
    ),
    secretaryName: firstText(
      project.secretaryName,
      details.secretaryName,
      profile.secretaryName,
      profile.name,
    ),
    landOwnershipType: firstText(
      land.landOwnershipType,
      project.landOwnershipType,
      project.landOwnership,
      details.landOwnership,
      "OWNED",
    ).toUpperCase(),
    landOwnerName: firstText(
      land.landOwnerName,
      project.landOwnerName,
      details.landOwnerName,
    ),
    surveyNo: firstText(
      land.surveyNo,
      project.surveyNo,
      project.landSurveyNo,
      details.surveyNo,
    ),
    khataNo: firstText(
      land.khataNo,
      land.khasraNo,
      project.khataNo,
      project.khasraNo,
      details.khasraNo,
    ),
    landArea: firstText(
      landAreaSqFt,
      land.landArea,
      project.landArea,
      details.landArea,
    ),
    landAreaUnit: landAreaSqFt
      ? "SQ_FEET"
      : firstText(
          land.landAreaUnit,
          project.landAreaUnit,
          details.landAreaUnit,
          "SQ_METER",
        ),
    leaseYears: firstNumber(
      land.leaseYears,
      project.leaseYears,
      details.leaseYears,
    ),
    capacityMt: firstNumber(
      project.proposedCapacityMt,
      project.capacityMt,
      project.capacity,
      details.capacityMt,
    ),
    estimatedProjectCost: firstNumber(
      project.pmcTechnicalDocuments?.PMC_PRELIMINARY_ESTIMATE
        ?.estimatedProjectCost,
      project.preliminaryEstimate?.estimatedProjectCost,
      project.estimatedProjectCost,
      project.projectCost,
      details.estimatedProjectCost,
    ),
    auditClass: firstText(
      project.auditClass,
      project.auditClassification,
      details.auditClass,
    ),
    latestAuditYear: firstText(
      project.latestAuditYear,
      project.lastAuditYear,
      details.lastAuditYear,
    ),
    netWorth: firstNumber(project.netWorth, details.netWorth),
    latestProfit: firstNumber(
      project.profitYear1,
      project.lastYearProfit,
      details.profitYear1,
    ),
    bankName: firstText(project.bankName, details.bankName),
    bankBranch: firstText(project.bankBranch, details.bankBranch),
    bankAccountNumber: firstText(
      project.accountNumber,
      project.bankAccountNumber,
      details.accountNumber,
    ),
  };
}

export function getWorkStartState(project = {}) {
  const status = firstText(
    project.workStatus,
    project.constructionStatus,
    project.executionStatus,
  ).toUpperCase();
  const workStartDate = firstText(
    project.workStartedAtIso,
    project.workStartDate,
    project.workStartedAt,
    project.constructionStartedAt,
  );
  const started = Boolean(
    workStartDate ||
      project.workStarted === true ||
      ["STARTED", "IN_PROGRESS", "CONSTRUCTION_STARTED"].includes(status) ||
      status.includes("WORK START") ||
      status.includes("CONSTRUCTION START"),
  );
  return { started, workStartDate, status };
}

export function getSubsidyUnlockState(project = {}) {
  const work = getWorkStartState(project);
  const subsidy = project.subsidyFirstInstallment || {};
  const disbursedAmount = firstNumber(
    subsidy.firstDisbursedAmount,
    project.firstLoanDisbursedAmount,
    project.firstDisbursementAmount,
  );
  const proofUrl = firstText(
    subsidy.documents?.FIRST_DISBURSEMENT_PROOF?.url,
    subsidy.firstDisbursementProofUrl,
    project.firstLoanDisbursementProofUrl,
  );
  const reasons = [];
  if (!work.started) reasons.push("PMC-confirmed work start is pending.");
  if (!disbursedAmount)
    reasons.push("First bank-loan disbursement amount is pending.");
  if (!proofUrl)
    reasons.push("First bank-loan disbursement proof is pending.");
  return {
    unlocked: reasons.length === 0,
    reasons,
    work,
    disbursedAmount,
    proofUrl,
  };
}

const normalizeRecord = (record, fallbackCode, fallbackName) => {
  if (!record || typeof record !== "object") return null;
  const url = firstText(record.url, record.downloadURL, record.fileUrl, record.photoUrl);
  if (!url) return null;
  return {
    code: firstText(record.code, record.type, record.documentType, fallbackCode),
    name: firstText(record.title, record.name, record.fileName, fallbackName),
    fileName: firstText(record.fileName, record.name, `${fallbackCode || "DOCUMENT"}.pdf`),
    url,
    storagePath: firstText(record.storagePath, record.path),
    status: firstText(record.status, record.verificationStatus),
  };
};

export function collectProjectDocumentRecords(project = {}) {
  const records = [];
  const seen = new Set();
  const add = (record, code, name) => {
    const normalized = normalizeRecord(record, code, name);
    if (!normalized) return;
    const key = normalized.storagePath || normalized.url;
    if (seen.has(key)) return;
    seen.add(key);
    records.push(normalized);
  };

  const arrays = [
    project.documents,
    project.uploadedDocuments,
    project.applicationDocuments,
    project.pacsDocuments,
    project.technicalDocuments,
    project.dprDocuments,
    project.tenderDocuments,
  ];
  arrays.forEach((items) => {
    if (Array.isArray(items)) items.forEach((item) => add(item));
  });

  const maps = [
    project.signedPacsDocuments,
    project.generatedDocuments,
    project.landLeaseDocuments,
    project.authorisedSignatures,
    project.subsidyFirstInstallment?.documents,
    project.registrarFileDocuments,
    project.pmcTechnicalDocuments,
    project.registrarDocumentSnapshot?.documents,
  ];
  maps.forEach((map) => {
    if (!map || typeof map !== "object") return;
    Object.entries(map).forEach(([key, item]) => add(item, key, key));
  });

  add(project.finalDpr, "DPR", "Final DPR");
  add(project.publishedDpr, "DPR", "Published DPR");
  add(project.mouDocument, "MOU", "Executed MOU");
  add(project.workOrderDocument, "WORK_ORDER", "Work Order");
  add(project.committeeEvidence, "COMMITTEE_EVIDENCE", "Committee Evidence");

  return records;
}

export function findProjectDocument(records, ...terms) {
  const needles = terms.map((term) => cleanText(term).toUpperCase()).filter(Boolean);
  if (!needles.length) return null;
  return records.find((record) => {
    const haystack = `${record.code} ${record.name} ${record.fileName}`.toUpperCase();
    return needles.some((needle) => haystack.includes(needle));
  });
}
